import { Activity, ActivityDraft, Metric } from '../core/activity.model';
import { DEMO_ACTIVITIES } from '../home-page/homepage/demo-activities';

/**
 * Seed data + simulated crawl for demo mode. Everything runs on this
 * in-memory dataset — no login, no API. Ids use the `local-` prefix so
 * ActivityDetailService treats every edit as non-persistable (client-side).
 *
 * The seed = Sani's real activities (DEMO_ACTIVITIES) plus real, popular
 * Angular/React/AI content standing in for platforms not covered there.
 */

/** ISO date n days before now — keeps the period filters (week/month) lively. */
function daysAgo(n: number, hour = 10): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

/** Well-known tech content covering platforms the personal set doesn't. */
function popularActivities(): Activity[] {
  return [
    {
      title: 'Andrew Ng — why AI agents will change how we build software',
      type: 'other',
      source: 'linkedin',
      date: daysAgo(2),
      created_date: daysAgo(2, 14),
      url: 'https://www.linkedin.com/in/andrewyng/',
      tags: ['ai', 'agents'],
      metrics_list: [
        {
          captured_date: daysAgo(2, 14),
          metrics: [
            { label: 'impressions', reach: 124000, is_manual: false },
            { label: 'reactions', reach: 3900, is_manual: false },
            { label: 'comments', reach: 410, is_manual: false },
          ],
          fetched_via: 'jina+claude',
        },
      ],
    },
    {
      title: 'ThePrimeagen — live: React & TypeScript performance deep-dive',
      type: 'livestream',
      source: 'twitch',
      date: daysAgo(5, 19),
      created_date: daysAgo(5, 22),
      url: 'https://www.twitch.tv/theprimeagen',
      tags: ['react', 'typescript'],
      metrics_list: [
        {
          captured_date: daysAgo(5, 22),
          metrics: [
            { label: 'views', reach: 15200, is_manual: false },
            { label: 'peak live', reach: 4200, is_manual: false },
          ],
          fetched_via: 'jina+claude',
        },
      ],
    },
    {
      title: 'The Coding Sloth — AI coding assistants explained in 60 seconds',
      type: 'video',
      source: 'tiktok',
      date: daysAgo(12),
      created_date: daysAgo(11),
      url: 'https://www.tiktok.com/@thecodingsloth',
      tags: ['ai', 'shorts'],
      metrics_list: [
        // Two snapshots — shows reach being re-measured over time.
        {
          captured_date: daysAgo(11),
          metrics: [
            { label: 'views', reach: 312000, is_manual: false },
            { label: 'likes', reach: 28400, is_manual: false },
          ],
          fetched_via: 'yt_dlp',
        },
        {
          captured_date: daysAgo(3),
          metrics: [
            { label: 'views', reach: 486000, is_manual: false },
            { label: 'likes', reach: 41200, is_manual: false },
          ],
          fetched_via: 'yt_dlp',
        },
      ],
    },
    {
      title: 'Understanding React Server Components — a visual guide',
      type: 'other',
      source: 'devto',
      date: daysAgo(18),
      created_date: daysAgo(17),
      url: 'https://dev.to/t/react',
      tags: ['react', 'web'],
      metrics_list: [
        {
          captured_date: daysAgo(17),
          metrics: [
            { label: 'views', reach: 42100, is_manual: false },
            { label: 'reactions', reach: 1350, is_manual: false },
          ],
          fetched_via: 'jina+claude',
        },
      ],
    },
    {
      title:
        'The Pragmatic Engineer — how AI tools are changing engineering teams',
      type: 'other',
      source: 'substack',
      date: daysAgo(26),
      created_date: daysAgo(25),
      url: 'https://pragmaticengineer.substack.com',
      tags: ['ai', 'engineering'],
      metrics_list: [
        {
          captured_date: daysAgo(25),
          metrics: [
            { label: 'views', reach: 88000, is_manual: false },
            { label: 'likes', reach: 2100, is_manual: false },
          ],
          fetched_via: 'jina+claude',
        },
      ],
    },
  ];
}

/** A fresh, deep copy every time — resetting the demo really resets it. */
export function seedDemoActivities(): Activity[] {
  // Personal activities first — they are the story; populars fill the gaps.
  const all = [...structuredClone(DEMO_ACTIVITIES), ...popularActivities()];
  return all.map((a, i) => ({
    ...a,
    _id: `local-demo-${i + 1}`,
  }));
}

// ── Simulated crawl — answers the quick-add draft entirely in-browser ──

interface SourceProfile {
  test: RegExp;
  source: string;
  type: Activity['type'];
  title: string;
  fetched_via: string;
  /** Base metrics; each gets a per-URL variance so repeat pastes differ. */
  metrics: Metric[];
}

const SOURCE_PROFILES: SourceProfile[] = [
  {
    test: /youtube\.com|youtu\.be/,
    source: 'youtube',
    type: 'video',
    title: 'My latest YouTube video',
    fetched_via: 'yt_dlp',
    metrics: [
      { label: 'views', reach: 48200 },
      { label: 'likes', reach: 1240 },
      { label: 'comments', reach: 96 },
    ],
  },
  {
    test: /(^|\.)(x|twitter)\.com/,
    source: 'x',
    type: 'other',
    title: 'My post on X',
    fetched_via: 'yt_dlp',
    metrics: [
      { label: 'impressions', reach: 12400 },
      { label: 'likes', reach: 320 },
      { label: 'reposts', reach: 41 },
    ],
  },
  {
    test: /linkedin\.com|lnkd\.in/,
    source: 'linkedin',
    type: 'other',
    title: 'My LinkedIn post',
    fetched_via: 'jina+claude',
    metrics: [
      { label: 'impressions', reach: 8750 },
      { label: 'reactions', reach: 214 },
    ],
  },
  {
    test: /tiktok\.com/,
    source: 'tiktok',
    type: 'video',
    title: 'My TikTok video',
    fetched_via: 'yt_dlp',
    metrics: [
      { label: 'views', reach: 92000 },
      { label: 'likes', reach: 8100 },
    ],
  },
  {
    test: /twitch\.tv/,
    source: 'twitch',
    type: 'livestream',
    title: 'My Twitch stream',
    fetched_via: 'jina+claude',
    metrics: [
      { label: 'views', reach: 3400 },
      { label: 'peak live', reach: 260 },
    ],
  },
  {
    test: /spotify\.com|podcasts\.apple\.com/,
    source: 'spotify',
    type: 'podcast',
    title: 'My podcast episode',
    fetched_via: 'jina+claude',
    metrics: [{ label: 'plays', reach: 4800 }],
  },
  {
    test: /medium\.com/,
    source: 'medium',
    type: 'other',
    title: 'My Medium article',
    fetched_via: 'jina+claude',
    metrics: [
      { label: 'views', reach: 5600 },
      { label: 'reads', reach: 1900 },
    ],
  },
  {
    test: /dev\.to/,
    source: 'devto',
    type: 'other',
    title: 'My DEV article',
    fetched_via: 'jina+claude',
    metrics: [
      { label: 'views', reach: 4300 },
      { label: 'reactions', reach: 180 },
    ],
  },
  {
    test: /meetup\.com/,
    source: 'meetup',
    type: 'speaking',
    title: 'My meetup talk',
    fetched_via: 'jina+claude+jsonld',
    metrics: [{ label: 'attendees', reach: 120, is_manual: true }],
  },
  {
    test: /instagram\.com/,
    source: 'instagram',
    type: 'video',
    title: 'My Instagram reel',
    fetched_via: 'jina+claude',
    metrics: [
      { label: 'views', reach: 21500 },
      { label: 'likes', reach: 1850 },
    ],
  },
];

const WEB_PROFILE: SourceProfile = {
  test: /./,
  source: 'web',
  type: 'other',
  title: 'My blog post',
  fetched_via: 'jina+claude',
  metrics: [{ label: 'views', reach: 2300 }],
};

/** Small deterministic per-URL variance (0.8–1.3×) so demos feel alive. */
function variance(url: string): number {
  let h = 0;
  for (let i = 0; i < url.length; i++) h = (h * 31 + url.charCodeAt(i)) >>> 0;
  return 0.8 + (h % 50) / 100;
}

/** "Crawl" a pasted URL: pick the platform profile and scale its numbers. */
export function simulateCrawlDraft(url: string): ActivityDraft {
  let host = url;
  try {
    host = new URL(url).hostname;
  } catch {
    // not a parseable URL — fall through to the generic web profile
  }
  const profile = SOURCE_PROFILES.find((p) => p.test.test(host)) ?? WEB_PROFILE;
  const v = variance(url);
  return {
    url,
    title: profile.title,
    type: profile.type,
    source: profile.source,
    date: new Date().toISOString(),
    metrics: profile.metrics.map((m) => ({
      ...m,
      reach: Math.round(m.reach * v),
      is_manual: m.is_manual ?? false,
    })),
    reach_known: true,
    fetched_via: profile.fetched_via,
    fetch_duration_ms: Math.round(600 + v * 900),
  };
}

/**
 * A small fixed insight feed for the demo. Refs point at seeded activities
 * (local-demo-1..N) so the "view the post" link resolves. This is illustrative
 * sample content, not engine output.
 */
export interface DemoInsight {
  _id: string;
  owner_user_id: string;
  kind: 'win' | 'watch_out' | 'suggestion';
  group: string;
  title: string;
  detail: string;
  evidence: string[];
  confidence: 'low' | 'medium' | 'high';
  score: number;
  rationale: {
    summary: string;
    steps: { observation: string; evidence: string; conclusion: string }[];
    caveats: string[];
  };
  signals: string[];
  refs: string[];
  state: string;
  created_at: string;
  updated_at: string;
}

export function seedDemoInsights(): DemoInsight[] {
  const now = '2026-07-20T00:00:00.000Z';
  const base = { owner_user_id: 'demo', state: 'new', created_at: now, updated_at: now };
  return [
    {
      ...base,
      _id: 'demo-insight-1',
      kind: 'suggestion',
      group: 'format',
      title: 'Turn your 60-second explainer into a full walkthrough',
      detail:
        'Your short explainer pulled 3.4x the views of your long-form videos, and three comments asked for "the full version".',
      evidence: [
        'Short "AI coding assistants explained in 60 seconds" reached far more viewers than your deep-dives',
        'Comments explicitly requested a longer cut',
      ],
      confidence: 'high',
      score: 68,
      rationale: {
        summary: 'A short is doing the discovery work a long video could convert.',
        steps: [
          {
            observation: 'The 60-second explainer outperformed the deep-dives on views',
            evidence: 'metrics across your last five posts',
            conclusion: 'The topic has reach; the format is capping the depth',
          },
        ],
        caveats: ['Based on five posts, so treat as a direction not a rule'],
      },
      signals: ['metrics', 'comments'],
      refs: ['local-demo-3'],
    },
    {
      ...base,
      _id: 'demo-insight-2',
      kind: 'win',
      group: 'audience',
      title: 'Your live coding sessions build the most conversation',
      detail:
        'The live React and TypeScript session drew more comments per viewer than anything else you posted.',
      evidence: [
        'Highest comments-per-view of your recent posts',
        'Viewers asked follow-up questions rather than just reacting',
      ],
      confidence: 'medium',
      score: 54,
      rationale: {
        summary: 'Live format turns viewers into participants.',
        steps: [
          {
            observation: 'The live session led on comments-per-view',
            evidence: 'comments on the live session',
            conclusion: 'Live is where your audience wants to engage, not just watch',
          },
        ],
        caveats: [],
      },
      signals: ['metrics', 'comments'],
      refs: ['local-demo-2'],
    },
    {
      ...base,
      _id: 'demo-insight-3',
      kind: 'watch_out',
      group: 'packaging',
      title: 'Your longest video has no call to action',
      detail:
        'The 40-minute deep-dive ends without pointing viewers anywhere, so the attention it earns goes nowhere.',
      evidence: ['No CTA in the closing two minutes of the transcript'],
      confidence: 'medium',
      score: 41,
      rationale: {
        summary: 'Attention earned but not directed.',
        steps: [
          {
            observation: 'The closing has no next step for the viewer',
            evidence: 'the video transcript',
            conclusion: 'A single clear CTA would capture the attention you already have',
          },
        ],
        caveats: [],
      },
      signals: ['transcript'],
      refs: ['local-demo-1'],
    },
  ];
}
